// Pure helpers for the recorder's remote-participant stats panel.
// No React, no VideoSDK — data → display transforms, unit-tested in isolation.

export const LOW_BITRATE_KBPS = 30; // matches the existing recorder/client watchdog

export const RATING = {
  EXCELLENT: "Excellent",
  GOOD: "Good",
  FAIR: "Fair",
  POOR: "Poor",
};

export const EMPTY_LIMITATIONS = { cpu: false, bandwidth: false, congestion: false };

// packetsLost / totalPackets -> integer percent 0..100. 0 when no packets seen.
export function packetLossPercent(packetsLost, totalPackets) {
  const lost = Number(packetsLost) || 0;
  const total = Number(totalPackets) || 0;
  if (total <= 0) return 0;
  const pct = (lost / total) * 100;
  if (pct < 0) return 0;
  if (pct > 100) return 100;
  return Math.round(pct);
}

// VideoSDK stat `size` is loosely typed; probe {width,height} or {w,h}.
export function formatResolution(size) {
  const width = size?.width ?? size?.w ?? null;
  const height = size?.height ?? size?.h ?? null;
  if (!width || !height) return "—";
  const tier =
    height >= 1080 ? "FHD" : height >= 720 ? "HD" : height >= 480 ? "SD" : "LD";
  return `${tier} (${width} × ${height})`;
}

// Frame rate isn't in the SDK's typed shape; probe likely runtime keys.
export function extractFps(stat) {
  const fps =
    stat?.size?.framerate ?? stat?.fps ?? stat?.framerate ?? stat?.frameRate;
  return typeof fps === "number" ? Math.round(fps) : null;
}

// Bitrate is already kbps in this SDK (the watchdog compares against 30 kbps).
export function extractBitrateKbps(stat) {
  const b = stat?.bitrate;
  return typeof b === "number" ? Math.round(b) : null;
}


export function getQualityScore(stat) {
  const lost = Number(stat?.packetsLost) || 0;
  const total = Number(stat?.totalPackets) || 0;
  const lossRatio = total > 0 ? lost / total : 0;
  const jitter = Number(stat?.jitter) || 0;
  const rtt = Number(stat?.rtt) || 0;

  const penalty = (value, scale, max) => Math.min((value / scale) * max, max);

  let score = 100;
  score -= penalty(lossRatio, 1, 50);
  score -= penalty(jitter, 30, 25);
  score -= penalty(rtt, 300, 25);
  return Math.max(0, score) / 10;
}

// Score -> label. The 7 and 4 cut-offs are the panel's green / amber / red
// bands; EXCELLENT just splits the top of the green band.
export function ratingFromScore(score) {
  if (score >= 9) return RATING.EXCELLENT;
  if (score > 7) return RATING.GOOD;
  if (score > 4) return RATING.FAIR;
  return RATING.POOR;
}

export function rateVideo(stat) {
  if (!stat) return null;
  return ratingFromScore(getQualityScore(stat));
}

export function rateAudio(stat) {
  if (!stat) return null;
  return ratingFromScore(getQualityScore(stat));
}

// Badge helper: everything but POOR is "good" (green check).
export function isGoodRating(rating) {
  return rating != null && rating !== RATING.POOR;
}

// Which of the three colours the panel paints a rating: green, amber, red.
// FAIR is advisory — degraded but usable — so it warns rather than fails.
export function ratingTone(rating) {
  if (rating === RATING.POOR) return "bad";
  if (rating === RATING.FAIR) return "warn";
  return "good";
}

// onQualityLimitation reducer. active = {cpu,bandwidth,congestion} booleans.
export function reduceActiveLimitations(active, event) {
  const base = active || EMPTY_LIMITATIONS;
  const type = event?.type;
  if (type !== "cpu" && type !== "bandwidth" && type !== "congestion") {
    return base;
  }
  return { ...base, [type]: event.state === "detected" };
}

export function deriveQualityStatuses(active) {
  const a = active || EMPTY_LIMITATIONS;
  return {
    cpuStatus: a.cpu ? "High" : "Normal",
    networkStatus: a.bandwidth || a.congestion ? "Unstable" : "Stable",
  };
}

// Read a 0..1 or 0..100 score from `obj` under any of `keys` -> 0..100 int | null.
export function extractScorePercent(obj, keys) {
  if (!obj) return null;
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === "number" && !Number.isNaN(v)) {
      const pct = v <= 1 ? v * 100 : v;
      return Math.round(Math.max(0, Math.min(100, pct)));
    }
  }
  return null;
}

// Chrome reports devices as "Default - MacBook Air Speakers (Built-in)". The
// panel shows the device's own name, so drop the "Default - " prefix and a
// trailing "(Built-in)". Returns null when nothing meaningful is left.
export function formatDeviceLabel(label) {
  if (typeof label !== "string") return null;
  const cleaned = label
    .replace(/^\s*default\s*-\s*/i, "")
    .replace(/\s*\(\s*built[-\s]?in\s*\)\s*$/i, "")
    .replace(/\s+/g, " ")
    .trim();
  return cleaned || null;
}

// ---- face-verification API readers -------------------------------------
// Two independent endpoints, each returning a score AND its own verdict. Keep
// them as pure readers so the exact response keys are pinned by unit tests
// rather than buried in a fetch handler — the Liveness row rendered an em dash
// for every journey because the key list guessed wrong and nothing caught it.
//
// Beware the crossed names: the key called `accuracy` belongs to the LIVENESS
// endpoint, while the face match score arrives as `score`.

// POST /ai/v1/face-verification/detect-spoof
//   -> { spoof_detected: false, accuracy: 0.9934190511703491 }
// `accuracy` is a straight liveness confidence: high means real. It is NOT
// inverted when spoof_detected is true.
export function readLivenessResult(json) {
  if (!json) return null;
  return {
    score: extractScorePercent(json, [
      "accuracy",
      "liveness_score",
      "liveness",
      "score",
      "confidence",
    ]),
    isLive: json.spoof_detected !== true,
  };
}

// POST /ai/v1/face-verification/verify
//   -> { verified: true, score: 1 }
// `verified` is the server's own verdict and is what the pipeline acts on, so
// it — not a threshold on the score — decides whether the row reads as good.
export function readFaceMatchResult(json) {
  if (!json) return null;
  return {
    score: extractScorePercent(json, [
      "score",
      "match_score",
      "confidence",
      "similarity",
    ]),
    verified: json.verified === true,
  };
}

export const EMPTY_DEVICE_INFO = { microphone: null, speaker: null, camera: null };

const DEVICE_KEYS = ["microphone", "speaker", "camera"];

// DEVICE_INFO can arrive partially populated — the customer joins with the mic
// off, so `selectedMicrophoneDevice` is unset on the first publish. Merge so a
// later message that resolves a device can't be clobbered by an earlier null.
export function mergeDeviceInfo(prev, payload) {
  const base = { ...EMPTY_DEVICE_INFO, ...(prev || {}) };
  if (!payload) return base;
  const next = { ...base };
  for (const key of DEVICE_KEYS) {
    if (payload[key]) next[key] = payload[key];
  }
  return next;
}
